import { useCallback, useEffect } from "react";

import { jsxToNode } from "@/components/map-pin";
import GenericPin from "@/components/map-pin/generic-pin";
import MapKitScript from "@/components/mapkit-script";
import { useMapKitLoad } from "@/hooks/use-map-kit-load";
import { cn } from "@/lib/utils";

import type { LocationType } from "../../types";

interface MapProps {
  className?: string;
  currentLocation: LocationType | null;
  hoveredLocationId: string | null;
  locations: LocationType[];
  onLocation(location: LocationType): void;
}

export default function MapKit({
  className,
  currentLocation,
  hoveredLocationId,
  locations,
  onLocation,
}: MapProps) {
  const { isMapInitialized, mapRef, mapContainerRef, handleMapKitLoad } =
    useMapKitLoad({ wheelZoom: true });

  const createMarker = useCallback(
    (location: LocationType, isActive: boolean) => {
      if (!location.location) {
        return null;
      }

      const [lng, lat] = location.location.coordinates;
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      const marker = new mapkit.Annotation(
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        new mapkit.Coordinate(lat, lng),
        (_coordinate, _options) =>
          jsxToNode(
            // biome-ignore lint/a11y/useKeyWithClickEvents: its ok
            // biome-ignore lint/a11y/noStaticElementInteractions: its ok
            // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
            <div onClick={() => onLocation(location)}>
              <GenericPin isActive={isActive} />
            </div>
          ),
        {
          data: {
            isActive,
            locationId: location.id,
          },
          size: { height: 26, width: 26 },
        }
      );
      return marker;
    },
    [onLocation]
  );

  const addAnnotations = useCallback(() => {
    if (!mapRef.current) {
      return;
    }

    mapRef.current.removeAnnotations(mapRef.current.annotations);

    const markers = locations
      .map((location) => {
        const isActive =
          currentLocation?.id === location.id ||
          hoveredLocationId === location.id;
        return createMarker(location, isActive);
      })
      .filter(Boolean) as mapkit.Annotation[];

    mapRef.current.addAnnotations(markers);

    const isMobile = window.innerWidth < 768;
    mapRef.current.showItems(markers, {
      animate: true,
      padding: isMobile
        ? // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          new mapkit.Padding(80, 40, 350, 40)
        : // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          new mapkit.Padding(80, 80, 80, 80),
    });
  }, [locations, currentLocation, hoveredLocationId, createMarker, mapRef]);

  useEffect(() => {
    if (!(isMapInitialized && mapContainerRef.current) || !!mapRef.current) {
      return;
    }

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapRef.current = new mapkit.Map(mapContainerRef.current, {
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      colorScheme: mapkit.Map.ColorSchemes.Dark,
      isRotationEnabled: false,
      isScrollEnabled: true,
      isZoomEnabled: true,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      mapType: mapkit.Map.MapTypes.MutedStandard,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsMapTypeControl: false,
    });

    addAnnotations();
  }, [isMapInitialized, addAnnotations, mapContainerRef.current, mapRef]);

  const getMarkerUpdates = useCallback(
    (annotations: mapkit.Annotation[]) => {
      const removeMarkers: mapkit.Annotation[] = [];
      const newMarkers: mapkit.Annotation[] = [];

      for (const marker of annotations) {
        const locationId = marker.data?.locationId;
        if (!locationId) {
          continue;
        }

        const shouldBeActive =
          currentLocation?.id === locationId ||
          hoveredLocationId === locationId;
        if (marker.data.isActive === shouldBeActive) {
          continue;
        }

        const location = locations.find((loc) => loc.id === locationId);
        if (!location) {
          continue;
        }

        removeMarkers.push(marker);
        const newMarker = createMarker(location, shouldBeActive);
        if (newMarker) {
          newMarkers.push(newMarker);
        }
      }

      return { newMarkers, removeMarkers };
    },
    [currentLocation, hoveredLocationId, locations, createMarker]
  );

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const { removeMarkers, newMarkers } = getMarkerUpdates([
      ...mapRef.current.annotations,
    ]);

    if (removeMarkers.length > 0) {
      mapRef.current.removeAnnotations(removeMarkers);
    }
    if (newMarkers.length > 0) {
      mapRef.current.addAnnotations(newMarkers);
    }
  }, [getMarkerUpdates, mapRef]);

  return (
    <div className={cn("h-full w-full md:w-1/2", className)}>
      <div
        className="h-full [&_canvas]:contrast-140 [&_canvas]:grayscale"
        ref={mapContainerRef}
      />
      <MapKitScript onLoad={handleMapKitLoad} />
    </div>
  );
}
